import { CdkStep, StepperSelectionEvent } from '@angular/cdk/stepper';
import { Component, Inject, Input, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatStepper } from '@angular/material/stepper';
import { HubFeature, PortalType } from '@ats/api/cm';
import { CmAuthService, CmFeatureService } from '@ats/cm/core';
import { LOC_INJECTOR, LocalizationProvider } from '@cm/core/localization';
import { SettingsService } from '@cm/core/settings';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable, of, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith } from 'rxjs/operators';
import {
	CPCreqUploadLocKeys,
	cpCreqUploadLocs,
	SPCreqUploadLocKeys,
	spCreqUploadLocs,
} from './creq-upload.locs';
import { CmCreqUploadService } from './creq-upload.service';
import { DEFAULT_DOCUMENTATION_VALIDATORS } from './default-documentation-validators';
import { UploadFeatures } from './selection-form/selection-form.component';

// Upload form shows invalid when starting over - Bug in the material stepper
// https://github.com/angular/components/issues/14067

@UntilDestroy()
@Component({
    selector: 'cm-creq-upload',
    templateUrl: './creq-upload.component.html',
    styleUrls: ['./creq-upload.component.scss'],
    providers: [LocalizationProvider({ ...spCreqUploadLocs, ...cpCreqUploadLocs })],
    standalone: false
})
export class CmCreqUploadComponent implements OnInit {
	@Input() cmcId$: Observable<number>;
	features$: Observable<UploadFeatures[]>;
	complete$: Observable<boolean>;
	hasAdvancedFeature$: Observable<boolean>;
	itemSelectionFormGroup: UntypedFormGroup;
	itemUploadFormGroup: UntypedFormGroup;

	private completeSubj$ = new Subject<boolean>();
	private stepTwoActivated = new Subject<CdkStep>();
	private selectionHasChanged = false;

	constructor(
		private readonly fb: UntypedFormBuilder,
		private readonly settings: SettingsService,
		private readonly features: CmFeatureService,
		private readonly uploadService: CmCreqUploadService,
		private readonly auth: CmAuthService,
		@Inject(LOC_INJECTOR) public readonly locs: CPCreqUploadLocKeys & SPCreqUploadLocKeys
	) {}

	ngOnInit() {
		this.hasAdvancedFeature$ = this.auth.guard$.pipe(
			map(guard => guard.reset().everyFeature([HubFeature.OtherRequirementAdvancedSelection]).all),
		);
		this.features$ = this.findPath();
		this.complete$ = this.completeSubj$.asObservable().pipe(startWith(false));
		this.initForm();

		this.uploadService.selection$
			.pipe(
				distinctUntilChanged(
					(a, b) =>
						a.credential === b.credential &&
						a.group === b.group &&
						a.intent === b.intent &&
						a.name === b.name &&
						a.overrideGroup === b.overrideGroup &&
						a.overrideName === b.overrideName
				),
				untilDestroyed(this)
			)
			.subscribe(() => (this.selectionHasChanged = true));

		this.stepTwoActivated
			.asObservable()
			.pipe(untilDestroyed(this))
			.subscribe(step => {
				if (this.selectionHasChanged && step.interacted) {
					step.interacted = false;

					this.itemUploadFormGroup.markAsPristine();
					this.itemUploadFormGroup.markAsUntouched();
				}
				this.selectionHasChanged = false;
			});
	}

	handleSelectionChange(ev: StepperSelectionEvent) {
		if (ev.selectedIndex === 1) {
			this.stepTwoActivated.next(ev.selectedStep);
		}
	}

	handleNext(stepper: MatStepper) {
		stepper.next();
	}

	handleCompletion(stepper: MatStepper) {
		this.completeSubj$.next(true);
		stepper.next();
	}

	handleRestart(stepper: MatStepper) {
		stepper.reset();
		this.itemUploadFormGroup.get('documentation').setValue(null);
		this.itemSelectionFormGroup.reset();
		this.itemUploadFormGroup.reset();
		this.itemSelectionFormGroup.markAsUntouched();
		this.itemSelectionFormGroup.markAsPristine();
		this.itemUploadFormGroup.markAsUntouched();
		this.itemUploadFormGroup.markAsPristine();
		this.selectionHasChanged = true;
		this.completeSubj$.next(false);
	}

	private findPath(): Observable<UploadFeatures[]> {
		const userFeatures$ = this.features.protected$.pipe(filter(features => !!features));
		return this.settings.portal === PortalType.SponsorPortal
			? of([])
			: userFeatures$.pipe(
					map(
						features =>
							features.filter(feature =>
								[
									UploadFeatures.Initial,
									UploadFeatures.Miscellaneous,
									UploadFeatures.Recertification,
								]
									.map(uploadFeat => uploadFeat.toString())
									.includes(feature)
							) as UploadFeatures[]
					)
				);
	}

	private initForm() {
		this.features$.pipe(untilDestroyed(this), distinctUntilChanged()).subscribe(features => {
			const intentValidators = features.length > 0 ? [Validators.required] : [];
			this.itemSelectionFormGroup = this.fb.group({
				intent: [null, intentValidators],
				credential: [null],
				group: [null, [Validators.required]],
				name: [null, [Validators.required]],
				overrideGroup: [{ value: null, disabled: true }, [Validators.required]],
				overrideName: [{ value: null, disabled: true }, [Validators.required]],
			});
		});

		this.itemUploadFormGroup = this.fb.group({
			activeDate: ['', Validators.required],
			overrideExpireDate: [false],
			expireDate: [null],
			points: [null],
			provider: [''],
			notes: [''],
			comments: [''],
			documentationLang: [''],
			documentation: ['', [Validators.required, ...DEFAULT_DOCUMENTATION_VALIDATORS]],
		});
	}
}
